import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { label } from "../../config/constants";
import { sendOtpForPasswordReset } from "../../shared/services/auth.service";
import { COUNTDOWN_SECONDS, OTP_LENGTH } from "./OtpConfig";



/**
 * OTP input + countdown UI for the in-app forgot password flow.
 *
 * Works in two modes:
 *  1. Modal mode  — InAppForgotPassword passes props and calls verifyHandler from its footer button
 *  2. Standalone  — OtpVerificationPage wraps this, reads URL params, and renders its own buttons
 * @author Sarthak Arora
 */
const OtpVerification = ({ selectedMethods = [], email = "", mobile = "", onOtpChange }) => {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const inputRefs = useRef([]);
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  /**
   * Updates the OTP state and calls the onOtpChange callback with the updated OTP string.
   * @author Sarthak Arora
   */
  const updateOtp = (updated) => {
    setOtp(updated);
    if (onOtpChange) onOtpChange(updated.join(""));
  };

  /**
   * Handles the change event for the OTP input.
   * @author Sarthak Arora
   */
  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    updateOtp(updated);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };


  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Handles the paste event for the OTP input.
   * @author Sarthak Arora
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    const updated = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => (updated[i] = ch));
    updateOtp(updated);
    const nextEmpty = pasted.length < OTP_LENGTH ? pasted.length : OTP_LENGTH - 1;
    inputRefs.current[nextEmpty]?.focus();
  };

  /**
   * Handles the resend event for the OTP input.
   * @author Sarthak Arora
   */
  const handleResend = async () => {
    await dispatch(sendOtpForPasswordReset({ methods: selectedMethods, email, mobile }));
    const cleared = Array(OTP_LENGTH).fill("");
    updateOtp(cleared);
    setSecondsLeft(COUNTDOWN_SECONDS);
    inputRefs.current[0]?.focus();
  };

  /**
   * Builds the description for the OTP input.
   * @author Sarthak Arora
   */
  const buildDescription = () => {
    const hasEmail = selectedMethods.includes("email");
    const hasMobile = selectedMethods.includes("mobile");
    if (hasEmail && hasMobile) {
      return (
        <>
          {label.ENTER_VERIFICATION_CODE_SENT_TO} {email} {label.OR} {mobile}
        </>
      );
    }
    if (hasEmail) {
      return (
        <>
          {label.ENTER_VERIFICATION_CODE_SENT_TO} {email ? email : label.YOUR_EMAIL_ADDRESS}
        </>
      );
    }
    return (
      <>
        {label.ENTER_VERIFICATION_CODE_SENT_TO} {mobile}
      </>
    );
  };

  return (
    <div className='otp-verification-container'>
      <p className='otp-description'>{buildDescription()}</p>

      <div className='otp-inputs' onPaste={handlePaste}>
        {otp.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type='text'
            inputMode='numeric'
            maxLength={1}
            value={digit}
            className='otp-input-box'
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
          />
        ))}
      </div>

      <p className='otp-timer'>
        {label.VERIFICATION_CODE_EXPIRES_IN}{" "}
        <strong>
          {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, "0")}
        </strong>{" "}
        {label.SECONDS}
      </p>

      <span
        className='otp-resend-link active'
        onClick={handleResend}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleResend()}
      >
        {label.RESEND_VERIFICATION_CODE}
      </span>
    </div>
  );
};

export default OtpVerification;

